import { useBrowserQuery } from '@/hooks/common'
import useAppStore from '@/store/useAppStore'
import { Box, Typography } from '@mui/material'
import { useEffect, useState, useMemo } from 'react'
import { shallow } from 'zustand/shallow'
import { useLocation, useNavigate } from 'react-router-dom'
import SearchTopicResults from './results/TopicResults'
import { ConnectionModel, ConnListAction, SearchConnection, Topic } from '@/types'
import { ScrollableCapsulesTabBar } from '@/components/ScrollableCapsulesTabBar'
import { PrivacyType, TopicType, TopicTypes } from '@/utils/constants'
import SearchConnectionResults from './results/ConnectionResults'
import CirclesSelectDrawer from '../user-profile/details/molecules/CirclesSelectDrawer'
import { useSubscription } from '@/context/SubscriptionContext'
import { FEATURE_KEYS } from '@/utils/subscriptions'
import NoDataPlaceholder from '@/components/NoDataPlaceholder'
import { SVGS } from '@/assets/svgs'

// const tabs = ['Patterns', 'Quilts', 'Galleries', 'Albums', 'People']
const tabs = [
  `${TopicTypes[TopicType.PATTERN].tabTitle}`,
  `${TopicTypes[TopicType.ALBUM].tabTitle}`,
  'People',
]
export default function SearchResult() {
  const navigate = useNavigate()
  const query = useBrowserQuery()
  const searchKey = query.get('key') || ''
  const forceUpdateKey = query.get('q') || '123'
  const { requirePro } = useSubscription()

  const { setSelectedTabIndex } = useAppStore()
  const [selectedTabIndex] = useAppStore((state) => [state.search.selectedTabIndex], shallow)

  const [index, setIndex] = useState(selectedTabIndex || 0)

  const [selectedUserForDrawer, setSelectedUserForDrawer] = useState<ConnectionModel | null>(null)

  const onClickTopic = (topic: Topic) => {
    const { topicID, topicType } = topic

    let previewQuery = new URLSearchParams()
    previewQuery.set('topicID', topicID)
    previewQuery.set('topicType', topicType)
    navigate(`/preview?${previewQuery.toString()}`)
  }

  const onClickUser = (user: SearchConnection) => {
    let previewQuery = new URLSearchParams()
    previewQuery.set('id', user.userID)
    navigate(`/profile/user?${previewQuery.toString()}`)
  }

  const onUserAction = (user: ConnectionModel, action: ConnListAction) => {
    if (action == ConnListAction.FOLLOW || action == ConnListAction.ACCEPT_CONN_REQUEST) {
      setSelectedUserForDrawer(user)
    }
  }

  const onSelectTabAtIndex = (index: number) => {
    const handleSelection = () => {
      setIndex(index)
      setSelectedTabIndex(index)
    }

    if (index == 1) {
      requirePro(FEATURE_KEYS.COLLECTIONS, handleSelection)
    } else handleSelection()
  }

  useEffect(() => {
    if (selectedTabIndex) {
      setIndex(selectedTabIndex)
    }
  }, [selectedTabIndex])

  const selectedTopicType = useMemo(() => {
    let type: TopicType = TopicType.PATTERN
    switch (index) {
      case 0:
        type = TopicType.PATTERN
        break
      case 1:
        type = TopicType.ALBUM
        break
      default:
        type = TopicType.PATTERN
    }
    return type
  }, [index])

  if (searchKey.length == 0) {
    return (
      <Box
        flexDirection={'column'}
        display={'flex'}
        sx={{
          width: '100%',
          backgroundColor: '#fafbfc',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <NoDataPlaceholder
          icon={SVGS.SearchIcon}
          title={`What are you looking for?`}
          subTitle={`Type a keyword to start searching`}
        />
      </Box>
    )
  }

  return (
    <Box
      flexDirection={'column'}
      display={'flex'}
      sx={{ width: '100%', backgroundColor: '#fafbfc', minHeight: '100vh' }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#ffffff',
        }}
      >
        <Box sx={{ px: '24px', py: '20px' }}>
          <ScrollableCapsulesTabBar
            titles={tabs}
            selectedIndex={index}
            onSelectAtIndex={onSelectTabAtIndex}
            showScrollButtons={false}
          />
        </Box>
        <Box sx={{ width: '100%', height: '1px', backgroundColor: '#e5e7eb' }} />
      </Box>

      {selectedTabIndex != tabs.length - 1 ? (
        <SearchTopicResults
          key={forceUpdateKey}
          searchKey={searchKey}
          onClickTopic={onClickTopic}
          filter={{ topicType: selectedTopicType, privacyType: PrivacyType.PUBLIC }}
        />
      ) : (
        <SearchConnectionResults
          key={forceUpdateKey}
          searchKey={searchKey}
          onClickUser={onClickUser}
          onUserAction={onUserAction}
        />
      )}

      <CirclesSelectDrawer
        open={selectedUserForDrawer != null}
        user={selectedUserForDrawer}
        onClose={() => setSelectedUserForDrawer(null)}
      />
    </Box>
  )
}
